import csvDownload from 'json-to-csv-export';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddAffiliate } from 'src/components/affiliate/AddAffiliate';
import { AffiliateRow } from 'src/components/affiliate/tables/AffiliateRow';
import { AffiliateTable } from 'src/components/affiliate/tables/AffiliateTable';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { EmptyTable } from 'src/components/utils/EmtpyTable';
import { useAffiliates } from 'src/hooks/useAffiliates';
import { useUser } from 'src/hooks/useUser';
import { IAffiliate } from 'src/models';
import { Routes } from 'src/router/Routes';
import AddIcon from '@mui/icons-material/Add';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import DownloadIcon from '@mui/icons-material/Download';
import { Button, Card, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

export const AffiliateOverview: FC = () => {
  const { t } = useTranslation();
  const [isAddAffiliateModalOpen, setIsAddAffiliateModalOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const partnerId = useUser()?.partnerId;
  let affiliates = useAffiliates(partnerId, reload);

  const handleCloseModal = () => {
    setIsAddAffiliateModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsAddAffiliateModalOpen(true);
  };

  const handleSaveModal = () => {
    setReload(!reload);
    setIsAddAffiliateModalOpen(false);
  };

  const handleDownload = () => {
    if (!affiliates) return;
    // filter name, email and tracking link from each affiliate
    const csvData = affiliates.map((affiliate: IAffiliate) => {
      return {
        internalAffiliate: affiliate.externalAffiliate ? 'false' : 'true',
        id: affiliate?.affiliateId,
        name: affiliate?.name,
        email: affiliate?.email,
        address: affiliate?.address ?? '',
        birthDate: affiliate?.birthDate ?? '',
        affiliateLink: affiliate?.affiliateLink,
      };
    });
    const dataToConvert = {
      data: csvData,
      filename: 'affiliate_report',
      delimiter: ';',
      headers: [
        t('affiliate.overview.table.internalAffiliate'),
        t('affiliate.overview.table.id'),
        t('affiliate.overview.table.name'),
        t('affiliate.overview.table.email'),
        t('affiliate.overview.table.address'),
        t('affiliate.overview.table.birthdate'),
        t('affiliate.overview.table.link'),
      ],
    };
    csvDownload(dataToConvert);
  };

  if (search) {
    affiliates = affiliates?.filter(
      (affiliate: IAffiliate) =>
        affiliate.affiliateLink?.includes(search) || affiliate.name?.includes(search) || affiliate.email?.includes(search),
    ) as IAffiliate[];
  }
  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ISSUER.ROOT,
            label: t('affiliate.overview.title'),
            icon: <Diversity2Icon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
        ]}
        headline={t('affiliate.overview.title')}
      >
        <Button variant="contained" disableElevation color="primary" startIcon={<AddIcon />} onClick={handleOpenModal}>
          {t('affiliate.overview.button.addAffiliate')}
        </Button>
      </SiteHeader>
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid flexGrow={1}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
              {t('affiliate.overview.affiliates')}
            </Typography>
          </Grid>
          <Grid>
            <Button variant="contained" disableElevation color="primary" startIcon={<DownloadIcon />} onClick={handleDownload}>
              {t('affiliate.overview.downloadCsv')}
            </Button>
          </Grid>
        </Grid>
        <TextField onChange={(e) => setSearch(e.target.value)} style={{ margin: '4px' }} label="search" />
        <AffiliateTable emptyElement={<EmptyTable bottomText={t('affiliate.overview.table.bottomText')} />}>
          {affiliates?.map((affiliate: IAffiliate, index: number) => (
            <AffiliateRow affiliate={affiliate} index={index} key={index} />
          ))}
        </AffiliateTable>
      </Card>
      {partnerId && (
        <AddAffiliate open={isAddAffiliateModalOpen} handleClose={handleCloseModal} handleSave={handleSaveModal} partnerId={partnerId} />
      )}
    </>
  );
};
